import type {
	ColorScheme,
	DiffColorScheme,
	DiffParticipantColorScheme,
	PublicColorScheme,
} from './types';

/** Green insertions, red deletions. */
export const traditionalScheme: DiffColorScheme = {
	insertColor: 'green',
	insertUnderlineStyle: 'solid',
	insertActiveColor: 'green',
	deleteColor: 'red',
	deleteActiveColor: 'red',
	deletedCellColor: 'gray',
	deletedCellOpacity: 0.5,
	deletedLozengeColor: 'gray',
	deletedLozengeTextTone: 'inverse',
	deletedMediaRingColor: 'red',
	deletedRowTreatment: 'strikeColor',
	insertedCellOpacity: 0.5,
	deletedQuoteNodeShape: 'ring',
	deletedCellBorderProminence: 'subtle',
	deletedBlockOutlineActiveEmphasis: 'background',
	addedCellOverlayZIndex: 1,
	roundedAddedCellOverlayInheritsBorder: true,
	insertedInlineTreatment: 'underline',
	deletedInlineTreatment: 'strikethrough',
	insertedNodeEmphasis: 'stateful',
	deletedNodeEmphasis: 'stateful',
	deletedQuoteNodeActiveTone: 'backgroundPressed',
	strikesDeletedEmbedCard: true,
};

/** Purple insertions, gray deletions (red on active). */
export const standardScheme: DiffColorScheme = {
	insertColor: 'purple',
	insertUnderlineStyle: 'dotted',
	insertActiveColor: 'purple',
	deleteColor: 'gray',
	deleteActiveColor: 'red',
	deletedCellColor: 'gray',
	deletedCellOpacity: 0.5,
	deletedLozengeColor: 'gray',
	deletedLozengeTextTone: 'inverse',
	// Red, not `deleteColor`'s gray: a11y-fixes rings deleted media red in both schemes.
	deletedMediaRingColor: 'red',
	deletedRowTreatment: 'textTint',
	insertedCellOpacity: 0.2,
	deletedQuoteNodeShape: 'borderLeft',
	deletedCellBorderProminence: 'accent',
	deletedBlockOutlineActiveEmphasis: 'border',
	addedCellOverlayZIndex: 2,
	roundedAddedCellOverlayInheritsBorder: false,
	insertedInlineTreatment: 'borderBottom',
	deletedInlineTreatment: 'glyphTint',
	insertedNodeEmphasis: 'static',
	deletedNodeEmphasis: 'static',
	deletedQuoteNodeActiveTone: 'borderAccent',
	strikesDeletedEmbedCard: false,
};

const createAttributionScheme = (color: DiffParticipantColorScheme): DiffColorScheme => ({
	...standardScheme,
	insertColor: color,
	insertActiveColor: color,
	deleteColor: color,
	deleteActiveColor: color,
	deletedCellColor: color,
});

/** Maps public schemes and attribution-only participant slots to their data objects. */
export const colorSchemeRegistry: Record<ColorScheme, DiffColorScheme> = {
	'red-bolder': createAttributionScheme('red-bolder'),
	'blue-bolder': createAttributionScheme('blue-bolder'),
	'green-bolder': createAttributionScheme('green-bolder'),
	'yellow-bolder': createAttributionScheme('yellow-bolder'),
	'purple-bolder': createAttributionScheme('purple-bolder'),
	'magenta-bolder': createAttributionScheme('magenta-bolder'),
	'teal-bolder': createAttributionScheme('teal-bolder'),
	'orange-bolder': createAttributionScheme('orange-bolder'),
	'lime-bolder': createAttributionScheme('lime-bolder'),
	'gray-bolder': createAttributionScheme('gray-bolder'),
	'blue-subtle': createAttributionScheme('blue-subtle'),
	'red-subtle': createAttributionScheme('red-subtle'),
	'orange-subtle': createAttributionScheme('orange-subtle'),
	'yellow-subtle': createAttributionScheme('yellow-subtle'),
	'green-subtle': createAttributionScheme('green-subtle'),
	'teal-subtle': createAttributionScheme('teal-subtle'),
	'purple-subtle': createAttributionScheme('purple-subtle'),
	'magenta-subtle': createAttributionScheme('magenta-subtle'),
	traditional: traditionalScheme,
	standard: standardScheme,
};

/** Attribution accents are impossible in the refactor-off cohort; fall back defensively. */
export const getLegacyColorScheme = (
	colorScheme: ColorScheme | undefined,
): PublicColorScheme | undefined =>
	colorScheme === 'standard' || colorScheme === 'traditional' ? colorScheme : undefined;
